import React, { ForwardRefRenderFunction, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Grid, Modal, Space, Spin } from 'antd';
import { CvTemplatePageLayout } from './CvTemplatePageLayout';
import {
  darkTemaplateTitles,
  TEMPLATE_COLORS,
  TEMPLATE_LIGHT_COLORS,
  TemplateModalFooter,
} from './TemplateModalFooter';
import { pdf, PDFViewer } from '@react-pdf/renderer';
import { NoSsr } from './NoSsr';
import { UserProfile } from '../../models/UserModels';
import { ResponsiveModal } from '../../components/ResponsiveModal/ResponsiveModal';
import { CvTemplateMobileView } from './CVTemplateMobileView';
import styles from './CvTemplate.module.less';
import { saveAs } from 'file-saver';
import { useCachedData } from '../../connection/CacheContext';
import Utils from '../../utils/Utils';
import Template1 from './templates/Template1';
import Template2 from './templates/Template2';
import Template3 from './templates/Template3';
import Template4 from './templates/Template4';
import Template5 from './templates/Template5';
import Template6 from './templates/Template6';
import Template7 from './templates/Template7';
import Template8 from './templates/Template8';
import Template9 from './templates/Template9';
import Template10 from './templates/Template10';
import Template11 from './templates/Template11';
import Template12 from './templates/Template12';
import Template13 from './templates/Template13';
import Template14 from './templates/Template14';
import Template15 from './templates/Template15';
import Template16 from './templates/Template16';
import Template17 from './templates/Template17';
import Template18 from './templates/Template18';
import Template19 from './templates/Template19';
import Template20 from './templates/Template20';
import Template21 from './templates/Template21';

export type CvTemplateModalInstance = {
  show: (value: UserProfile) => void;
};
export type CvTemplateData = {
  template: React.ElementType;
  title: string;
  image?: boolean;
};
type Props = {
  title?: string;
};
export const TEMPLATES: CvTemplateData[] = [
  {
    template: Template1,
    title: 'Template 1',
  },
  {
    template: Template2,
    title: 'Template 2',
  },
  {
    template: Template3,
    title: 'Template 3',
    image: true,
  },
  {
    template: Template4,
    title: 'Template 4',
    image: true,
  },
  {
    template: Template5,
    title: 'Template 5',
    image: true,
  },
  {
    template: Template6,
    title: 'Template 6',
    image: true,
  },
  {
    template: Template7,
    title: 'Template 7',
    image: true,
  },
  {
    template: Template8,
    title: 'Template 8',
  },
  {
    template: Template9,
    title: 'Template 9',
    image: true,
  },
  {
    template: Template10,
    title: 'Template 10',
  },
  {
    template: Template11,
    title: 'Template 11',
    image: true,
  },
  {
    template: Template12,
    title: 'Template 12',
  },
  {
    template: Template13,
    title: 'Template 13',
    image: true,
  },
  {
    template: Template14,
    title: 'Template 14',
  },
  {
    template: Template15,
    title: 'Template 15',
    image: true,
  },
  {
    template: Template16,
    title: 'Template 16',
  },
  {
    template: Template17,
    title: 'Template 17',
    image: true,
  },
  {
    template: Template18,
    title: 'Template 18',
  },
  {
    template: Template19,
    title: 'Template 19',
    image: true,
  },
  {
    template: Template20,
    title: 'Template 20',
  },
  {
    template: Template21,
    title: 'Template 21',
    image: true,
  },
];
export type ProfileActivity = { institute: string; description: string[] };
export type CvTemplateProfile = UserProfile & { activities: ProfileActivity[] };
const { useBreakpoint } = Grid;
const CvTemplateModal: ForwardRefRenderFunction<CvTemplateModalInstance, Props> = (props, ref) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [selectedColor, setSelectedColor] = useState<string>(TEMPLATE_COLORS[0]);
  const [selectedTemplate, setSelectedTemplate] = useState<CvTemplateData>(TEMPLATES[0]);
  const [profile, setProfile] = useState<CvTemplateProfile>();
  const { enumValue } = useCachedData();
  const bp = useBreakpoint();
  useImperativeHandle(
    ref,
    () => ({
      show: (value) => {
        const data: CvTemplateProfile = { ...value, activities: [] };
        for (const v of value.education ?? []) {
          if (v.description === undefined) continue;
          const idx = data.activities.findIndex((x) => x.institute === v.institute.value);
          if (idx === -1) {
            data.activities.push({ institute: v.institute.value, description: [v.description] });
          } else {
            data.activities[idx].description.push(v.description);
          }
        }
        if (data?.experience && data?.experience?.length > 0) {
          data.experience = data.experience.map((v) => {
            v.city = enumValue?.['Location']?.[v.city] ?? v.city;
            return v;
          });
        }

        if (data?.sector && data?.sector?.length > 0) {
          data.sector = data.sector?.map((value) => enumValue?.['Sector']?.[value]);
        }
        if (data?.jobType && data?.jobType?.length > 0) {
          data.jobType = data.jobType?.map((value) => enumValue?.['Job Types']?.[value]);
        }
        if (data?.jobCategory && data?.jobCategory?.length > 0) {
          data.jobCategory = data.jobCategory?.map((value) => enumValue?.['Job Category']?.[value]);
        }
        if (data?.languages && data?.languages.length > 0) {
          data.languages = data.languages.map((value) => ({
            language: enumValue?.['Language']?.[value.language],
            fluency: value.fluency,
          }));
        }
        setProfile(data);
        setVisible(true);
      },
    }),
    [enumValue],
  );
  const Comp = selectedTemplate.template;

  useEffect(() => {
    if (darkTemaplateTitles.includes(selectedTemplate.title)) {
      setSelectedColor(TEMPLATE_COLORS[0]);
    } else {
      switch (selectedTemplate.title) {
        case 'Template 2':
        case 'Template 3':
          setSelectedColor(TEMPLATE_LIGHT_COLORS[0]);
          break;
        case 'Template 4':
          setSelectedColor(TEMPLATE_LIGHT_COLORS[1]);
          break;
        case 'Template 5':
          setSelectedColor(TEMPLATE_LIGHT_COLORS[2]);
          break;
        case 'Template 7':
          setSelectedColor(TEMPLATE_LIGHT_COLORS[3]);
          break;
        case 'Template 6':
          setSelectedColor(TEMPLATE_LIGHT_COLORS[4]);
          break;
        case 'Template 9':
          setSelectedColor(TEMPLATE_LIGHT_COLORS[5]);
          break;
        default:
          setSelectedColor(TEMPLATE_LIGHT_COLORS[0]);
      }
    }
  }, [selectedTemplate]);

  return (
    <>
      {bp.lg ? (
        <Modal
          title={'Select Resume Template'}
          width={'100vw'}
          footer={
            <TemplateModalFooter
              onCancel={setVisible}
              onColorChange={setSelectedColor}
              selectedColor={selectedColor}
              selectedTemplateTitle={selectedTemplate.title}
              button={
                <NoSsr fallback={<Spin spinning={true} />}>
                  <Button
                    type="primary"
                    onClick={async () => {
                      const doc = <Comp selectedColor={selectedColor} profile={profile} />;
                      const asPdf = pdf(doc); // {} is important, throws without an argument
                      // asPdf.updateContainer(doc);
                      const blob = await asPdf.toBlob();
                      saveAs(
                        blob,
                        `${Utils.getFullName(profile?.firstName, profile?.lastName)}.pdf`,
                      );
                    }}>
                    Download
                  </Button>
                  {/*<PDFDownloadLink*/}
                  {/*  fileName={`${profile?.firstName} ${profile?.lastName}`}*/}
                  {/*  document={<Comp selectedColor={selectedColor} profile={profile} />}>*/}
                  {/*  <Button type={'primary'}>Download</Button>*/}
                  {/*</PDFDownloadLink>*/}
                </NoSsr>
              }
            />
          }
          style={{ top: 20 }}
          open={visible}
          onCancel={() => setVisible(false)}>
          <div
            style={{
              height: 'calc(100vh - 205px)',
              margin: -20,
              marginTop: -16,
              marginBottom: -25,
            }}>
            <CvTemplatePageLayout
              profileImage={profile?.picture}
              onChangeTemplate={setSelectedTemplate}
              selectedTemplateTitle={selectedTemplate.title}>
              <NoSsr fallback={<Spin spinning={true} />}>
                <PDFViewer showToolbar={false} width={'100%'} height={'100%'}>
                  <Comp profile={profile} selectedColor={selectedColor} />
                </PDFViewer>
              </NoSsr>
            </CvTemplatePageLayout>
          </div>
        </Modal>
      ) : (
        <ResponsiveModal
          title={'Select Resume Template'}
          open={visible}
          onCancel={() => setVisible(false)}
          footer={
            <div className={styles.mobileFooter}>
              <Space size={'middle'} align={'end'}>
                <Button onClick={() => setVisible(false)}>Cancel</Button>
                <NoSsr fallback={<Spin spinning={true} />}>
                  <Button
                    type="primary"
                    onClick={async () => {
                      const doc = <Comp selectedColor={selectedColor} profile={profile} />;
                      const asPdf = pdf(doc); // {} is important, throws without an argument
                      // asPdf.updateContainer(doc);
                      const blob = await asPdf.toBlob();
                      saveAs(
                        blob,
                        `${Utils.getFullName(profile?.firstName, profile?.lastName)}.pdf`,
                      );
                    }}>
                    Download
                  </Button>
                  {/*<PDFDownloadLink*/}
                  {/*  fileName={`${profile?.firstName} ${profile?.lastName}`}*/}
                  {/*  document={<Comp selectedColor={selectedColor} profile={profile} />}>*/}
                  {/*  <Button type={'primary'}>Download</Button>*/}
                  {/*</PDFDownloadLink>*/}
                </NoSsr>
              </Space>
            </div>
          }>
          <CvTemplateMobileView
            profileImage={profile?.picture}
            onChangeTemplate={setSelectedTemplate}
            selectedTemplateTitle={selectedTemplate.title}
          />
        </ResponsiveModal>
      )}
    </>
  );
};
export default React.forwardRef<CvTemplateModalInstance, Props>(CvTemplateModal);
